import { ClassEdge, type FlowEdge, type FlowNode, getParentNodeIds } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import type {
    EdgeMouseHandler,
    Node,
    NodeMouseHandler,
    OnConnect,
    OnConnectEnd,
    OnConnectStart,
    OnNodeDrag,
    OnNodesChange
} from '@xyflow/react'
import { applyNodeChanges, getConnectedEdges, useReactFlow } from '@xyflow/react'
import produce from 'immer'
// import { unionBy } from 'lodash-es'
import { useCallback, useRef } from 'react'

import { useFlow, useFlowContainerContext } from '@/contexts/FlowContext'

// import { CUSTOM_ITERATION_START_NODE } from '../nodes/iteration-start/constants'
// import { useWorkflowStore } from '../store'
import { useHelpline } from './useHelpLine'
import { useNodeIterationInteractions } from './useInteractions'

export const useNodesInteractions = () => {
    const { log, setIsChanged } = useFlowContainerContext()
    const readonly = !!log
    const { getEdges, getNodes, updateNode } = useReactFlow()
    const enteringNodeRef = useRef<FlowNode | null>(null)
    // const [enteringNode, setEnteringNode] = useState<FlowNode | null>(null)
    const sourceNodePayloadRef = useRef<{
        nodeId: string | null
        handleType: string | null
        handleId: string | null
    } | null>(null)
    const { setNodes, setEdges } = useFlow()
    const { setHelpLineHorizontal, setHelpLineVertical } = useFlow()
    // const workflowStore = useWorkflowStore()
    const { handleSetHelpline } = useHelpline()
    const { handleNodeIterationChildDrag } = useNodeIterationInteractions()
    const dragNodeStartPosition = useRef({ x: 0, y: 0 })

    const handleNodesChange = useCallback<OnNodesChange<FlowNode>>(
        changes => {
            setNodes?.(nodes => applyNodeChanges<FlowNode>(changes, nodes))
            if (changes.length > 0 && changes[0]) {
                const change = changes[0]
                if (change.type === 'select' || change.type === 'dimensions') {
                    return
                }
                setIsChanged?.(true)
            }
        },
        [setIsChanged, setNodes]
    )

    const handleNodeClick = useCallback<NodeMouseHandler<FlowNode>>(
        (e, node) => {
            const nodeId = node.id
            // updateNode(node.id, draft => {
            //     draft.selected = true
            //     return draft
            // })
            setNodes?.(draft =>
                draft.map(
                    node => (node.id === nodeId ? { ...node, selected: true } : { ...node, selected: false }) // 如果你要单选
                )
            )
        },
        [setNodes]
    )

    const handleEdgeClick = useCallback<EdgeMouseHandler<FlowEdge>>(
        (e, edge) => {
            const edgeId = edge.id
            // updateNode(node.id, draft => {
            //     draft.selected = true
            //     return draft
            // })
            setEdges?.(draft =>
                draft.map(
                    edge => (edge.id === edgeId ? { ...edge, selected: true } : { ...edge, selected: false }) // 如果你要单选
                )
            )
        },
        [setEdges]
    )

    const handleNodeDragStart = useCallback<OnNodeDrag>((_, node) => {
        dragNodeStartPosition.current = { x: node.position.x, y: node.position.y }
    }, [])

    const handleNodeDrag = useCallback<OnNodeDrag>(
        (e, node: Node) => {
            e.stopPropagation()
            const { restrictPosition } = handleNodeIterationChildDrag(node)

            const { showHorizontalHelpLineNodes, showVerticalHelpLineNodes } = handleSetHelpline(node)
            const showHorizontalHelpLineNodesLength = showHorizontalHelpLineNodes.length
            const showVerticalHelpLineNodesLength = showVerticalHelpLineNodes.length

            updateNode(node.id, draft => {
                draft.position.x = node.position.x
                draft.position.y = node.position.y
                if (showVerticalHelpLineNodesLength > 0) {
                    draft.position.x = showVerticalHelpLineNodes[0].position.x
                } else if (restrictPosition.x !== undefined) {
                    draft.position.x = restrictPosition.x
                }

                if (showHorizontalHelpLineNodesLength > 0) {
                    draft.position.y = showHorizontalHelpLineNodes[0].position.y
                } else if (restrictPosition.y !== undefined) {
                    draft.position.y = restrictPosition.y
                }
                return draft
            })
        },
        [handleNodeIterationChildDrag, handleSetHelpline, updateNode]
    )

    const handleNodeDragStop = useCallback<OnNodeDrag>(
        (_, node) => {
            const { x, y } = dragNodeStartPosition.current
            if (!(x === node.position.x && y === node.position.y)) {
                setHelpLineHorizontal?.()
                setHelpLineVertical?.()

                if (x !== 0 && y !== 0) {
                    // 以后要记录位置变动的undo,redo
                }
            }
        },
        [setHelpLineHorizontal, setHelpLineVertical]
    )

    const handleNodeEnter = useCallback<NodeMouseHandler>(
        (_, node) => {
            if (readonly) {
                return
            }
            enteringNodeRef.current = node as FlowNode
            setEdges?.(edges =>
                produce(edges, draft => {
                    const connectedEdges = getConnectedEdges([node], edges)
                    connectedEdges.forEach(edge => {
                        const currentEdge = draft.find(e => e.id === edge.id)
                        if (currentEdge) {
                            if (currentEdge.data) {
                                currentEdge.data.isNodeHovering = true
                            } else {
                                currentEdge.data = {
                                    isNodeHovering: true
                                }
                            }
                        }
                    })
                })
            )
        },
        [readonly, setEdges]
    )

    const handleNodeLeave = useCallback<NodeMouseHandler>(
        (_, node) => {
            setEdges?.(edges =>
                produce(edges, draft => {
                    draft.forEach(edge => {
                        if (edge.data) {
                            edge.data.isNodeHovering = false
                        }
                    })
                })
            )
        },
        [setEdges]
    )

    // 节点连接
    const handleNodeConnect = useCallback<OnConnect>(
        payload => {
            const { source, sourceHandle, target } = payload
            if (!source || !target) {
                return
            }
            if (source === target) {
                return
            }
            if (readonly) {
                return
            }
            const edges = getEdges() as FlowEdge[]
            // const targetNode = find(edge => edge.target === target, edges)
            // if (targetNode) {
            //     return
            // }
            const toParentAllNodeIds = getParentNodeIds(source, edges, [])
            if (toParentAllNodeIds?.includes(target)) {
                return
            }
            const newEdge: FlowEdge = {
                id: nanoid(),
                type: ClassEdge.Common,
                source,
                target,
                sourceHandle: sourceHandle || undefined
            }

            setEdges?.(draft => [...draft, newEdge])
            setIsChanged?.(true)
        },
        [getEdges, readonly, setEdges, setIsChanged]
    )

    const handleNodeConnectStart = useCallback<OnConnectStart>(
        (_, { nodeId, handleType, handleId }) => {
            if (readonly) {
                return
            }
            sourceNodePayloadRef.current = {
                nodeId,
                handleType,
                handleId
            }
        },
        [readonly]
    )

    const handleNodeConnectEnd = useCallback<OnConnectEnd>(
        (e: any) => {
            if (readonly || !sourceNodePayloadRef.current) {
                return
            }
            const { nodeId: sourceNodeId, handleType, handleId } = sourceNodePayloadRef.current
            if (!enteringNodeRef.current || !sourceNodeId) {
                return
            }
            // const { x, y } = screenToFlowPosition({ x: e.x, y: e.y })
            // if (handleType === 'source') {
            //     handleNodeConnect({
            //         source: sourceNodeId,
            //         sourceHandle: handleId,
            //         target: enteringNodeRef.current.id,
            //         targetHandle: '',
            //     })
            // }
            enteringNodeRef.current = null
            sourceNodePayloadRef.current = null
        },
        [readonly]
    )

    return {
        handleNodeClick,
        handleEdgeClick,
        handleNodesChange,
        handleNodeDragStart,
        handleNodeDrag,
        handleNodeDragStop,
        handleNodeEnter,
        handleNodeLeave,
        handleNodeConnectStart,
        handleNodeConnectEnd,
        handleNodeConnect
    }
}
